import { parse_boolean_falsy_types } from './index';

/**
 * Coerces a hidden input value only when the field declares an explicit
 * data-hub-value-type. Failed coercions fall back to the original string so
 * a malformed author-provided marker does not drop the key or hard-block form
 * submission.
 *
 * @param {string} raw_value
 * @param {string|null} declared_value_type
 * @returns {*}
 */
export function parse_declared_form_value(raw_value, declared_value_type) {
    if (!declared_value_type) return raw_value;

    const value_type = String(declared_value_type).trim().toLowerCase();
    const string_value = String(raw_value ?? '');
    const trimmed_value = string_value.trim();

    if ((value_type === 'int' || value_type === 'number' || value_type === 'float') && trimmed_value === '') {
        return string_value;
    }

    if (value_type === 'boolean') {
        const parsed_boolean = parse_boolean_falsy_types(trimmed_value);
        return typeof parsed_boolean === 'boolean'
            ? parsed_boolean
            : string_value;
    }

    if (value_type === 'int') {
        const parsed_int = Number(trimmed_value);
        return Number.isInteger(parsed_int) ? parsed_int : string_value;
    }

    if (value_type === 'number' || value_type === 'float') {
        const parsed_number = Number(trimmed_value);
        return Number.isFinite(parsed_number) ? parsed_number : string_value;
    }

    if (value_type === 'json') {
        try {
            return JSON.parse(string_value);
        } catch {
            return string_value;
        }
    }

    return string_value;
}

/**
 * Harvest marked form fields (metadata / custom_data) from container(s) into a flat object.
 * Shared internals for get_metadata_form_fields / get_custom_data_form_fields.
 *
 * Checkboxes submit their boolean checked state. Every other input
 * (text/hidden/tel/...) keeps `.value`, unless it declares a type via the
 * `data-hub-value-type` marker ("boolean" | "number" | "int" | "float" | "json", stamped by the
 * hidden-field renderer) — declared types coerce, everything else stays a string (no sniffing).
 *
 * @param {HTMLElement|NodeList|Array} form_container - Container element(s).
 * @param {string} selector - Marker attribute selector for the fields to harvest.
 * @returns {Object} Flat key/value object ({} when nothing matches).
 */
function harvest_marked_fields(form_container, selector) {
    let harvested = {};

    // Normalize to iterable: wrap single element in array
    const containers = (form_container instanceof NodeList || form_container.forEach)
        ? form_container
        : [form_container];

    containers.forEach(container => {
        const fields = container.querySelectorAll(selector);
        if (fields && fields.length > 0) {
            fields.forEach(field => {
                const key = field.id || field.name;
                if (!key) return;
                if (field.type === 'checkbox') {
                    harvested[key] = field.checked;
                } else {
                    const declared_value_type = field.getAttribute('data-hub-value-type');
                    harvested[key] = parse_declared_form_value(field.value, declared_value_type);
                }
            });
        }
    });

    return harvested;
}

/**
 * Retrieves metadata form fields from the given container.
 * NOTE: the selector lives on the data_hub_form instance (set in init from
 * settings.metadata_form_field_selector / default_selectors) — pass the FORM instance,
 * not the DataHub instance.
 * @param {HTMLElement|NodeList} form_container - Container element(s) for the form.
 * @returns {Object} An object of metadata key/value pairs.
 */
export function get_metadata_form_fields(form_container, data_hub_instance = window?.data_hub) {
    if (!form_container) {
        form_container = data_hub_instance?.form_containers
                        || document.querySelectorAll(data_hub_instance?.form_container_selector
                        || '.data-hub-form-container');
    }

    if (!data_hub_instance || !data_hub_instance.form_metadata_form_field_selector) {
        return {};
    }

    return harvest_marked_fields(form_container, data_hub_instance.form_metadata_form_field_selector);
}

/**
 * Retrieves custom_data form fields (data-hub-form-custom-data-field markers) from the
 * given container. Mirrors get_metadata_form_fields; same instance-passing rule applies.
 * @param {HTMLElement|NodeList} form_container - Container element(s) for the form.
 * @returns {Object} An object of custom_data key/value pairs.
 */
export function get_custom_data_form_fields(form_container, data_hub_instance = window?.data_hub) {
    if (!form_container) {
        form_container = data_hub_instance?.form_containers
                        || document.querySelectorAll(data_hub_instance?.form_container_selector
                        || '.data-hub-form-container');
    }

    if (!data_hub_instance || !data_hub_instance.form_custom_data_form_field_selector) {
        return {};
    }

    return harvest_marked_fields(form_container, data_hub_instance.form_custom_data_form_field_selector);
}
